<script lang="ts">
	import { page } from '$app/stores';
	import { locale } from 'svelte-i18n';
	import type { Language } from '$lib/docs/types';
	import type { PageData } from './$types';

	// Data is now provided server-side by +page.server.ts (load()), so the
	// <h1> tag name and article list are present in the prerendered HTML for
	// crawlers. No more onMount fetch / loading spinner for the primary content.
	let { data }: { data: PageData } = $props();

	const tagName = $derived($page.params.tag);
	const currentLocale = $derived(($locale as Language) || 'en');

	const tagInfo = $derived(data.tagInfo);
	const relatedTags = $derived(data.relatedTags);

	function formatDate(dateString: string): string {
		return new Date(dateString).toLocaleDateString(currentLocale, {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	}

	// Build a meta description long enough to be useful (Ahrefs: "Meta description
	// too short") from REAL data only — the tag's article count plus the actual
	// titles of its first few articles. No invented copy.
	const metaDescription = $derived.by(() => {
		if (!tagInfo) return `Heyweek documentation articles tagged #${tagName}.`;
		const titles = tagInfo.articles.slice(0, 3).map((a) => a.title);
		const plural = tagInfo.count === 1 ? 'article' : 'articles';
		const list = titles.length ? ` Including: ${titles.join(', ')}.` : '';
		const desc = `Browse ${tagInfo.count} Heyweek documentation ${plural} tagged #${tagInfo.name}.${list} Step-by-step guides, references, and tips for getting the most out of Heyweek.`;
		return desc.length > 158 ? desc.slice(0, 155).replace(/\s+\S*$/, '') + '…' : desc;
	});
</script>

<svelte:head>
	<title>{tagName ? `#${tagName} guides` : 'Tag'} - Heyweek Docs</title>
	<meta name="description" content={metaDescription} />
	<meta property="og:title" content={tagName ? `#${tagName} guides - Heyweek Docs` : 'Tag - Heyweek Docs'} />
	<meta property="og:description" content={metaDescription} />
</svelte:head>

<div class="tag-page">
	<div class="tag-page__container">
		<!-- Header -->
		<header class="tag-page__header">
			<div class="tag-page__breadcrumb">
				<a href="/docs" class="tag-page__breadcrumb-link">Documentation</a>
				<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
					<path d="M5.5 4L9.5 8L5.5 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
				</svg>
				<a href="/docs/tags" class="tag-page__breadcrumb-link">Tags</a>
				<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
					<path d="M5.5 4L9.5 8L5.5 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
				</svg>
				<span class="tag-page__breadcrumb-current">#{tagName}</span>
			</div>
			
			{#if tagInfo}
				<div class="tag-page__title-section">
					<h1 class="tag-page__title">
						<span class="tag-page__tag-badge">#{tagInfo.name}</span>
					</h1>
					<p class="tag-page__count">
						{tagInfo.count} article{tagInfo.count === 1 ? '' : 's'} tagged
					</p>
				</div>
			{/if}
		</header>
		
		{#if tagInfo}
			<!-- Related Tags -->
			{#if relatedTags.length > 0}
				<section class="tag-page__related">
					<h2 class="tag-page__section-title">Related Tags</h2>
					<div class="tag-page__related-tags">
						{#each relatedTags as tag}
							<a href="/docs/tags/{encodeURIComponent(tag)}" class="tag-page__related-tag">
								#{tag}
							</a>
						{/each}
					</div>
				</section>
			{/if}
			
			<!-- Articles List -->
			<section class="tag-page__articles">
				<h2 class="tag-page__section-title">Articles</h2>
				<div class="tag-page__articles-list">
					{#each tagInfo.articles as article}
						<article class="article-card">
							<a href="/docs/{article.slug}" class="article-card__link">
								<div class="article-card__content">
									<h3 class="article-card__title">{article.title}</h3>
									<p class="article-card__description">{article.description}</p>
									<div class="article-card__meta">
										<time class="article-card__date" datetime={article.lastUpdated}>
											{formatDate(article.lastUpdated)}
										</time>
									</div>
								</div>
								<svg class="article-card__arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
									<line x1="5" y1="12" x2="19" y2="12"></line>
									<polyline points="12 5 19 12 12 19"></polyline>
								</svg>
							</a>
						</article>
					{/each}
				</div>
			</section>
		{/if}
	</div>
</div>

<style>
	.tag-page {
		min-height: 100vh;
		padding: 2rem 1.5rem;
	}
	
	.tag-page__container {
		max-width: 900px;
		margin: 0 auto;
	}
	
	/* Breadcrumb */
	.tag-page__breadcrumb {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		margin-bottom: 2rem;
		font-size: 0.875rem;
	}
	
	.tag-page__breadcrumb-link {
		color: var(--text-tertiary);
		text-decoration: none;
		transition: color 0.2s ease;
	}
	
	.tag-page__breadcrumb-link:hover {
		color: var(--text-primary);
	}
	
	.tag-page__breadcrumb-current {
		color: var(--text-primary);
		font-weight: 500;
	}
	
	.tag-page__breadcrumb svg {
		color: var(--text-tertiary);
		width: 12px;
		height: 12px;
	}
	
	/* Loading State */
	.tag-page__loading {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 4rem 2rem;
		text-align: center;
	}
	
	.tag-page__spinner {
		width: 32px;
		height: 32px;
		border: 3px solid var(--border-light);
		border-top-color: var(--brand-primary);
		border-radius: 50%;
		animation: spin 0.8s linear infinite;
		margin-bottom: 1rem;
	}
	
	@keyframes spin {
		to { transform: rotate(360deg); }
	}
	
	.tag-page__loading p {
		color: var(--text-tertiary);
		font-size: 0.875rem;
	}
	
	/* Error State */
	.tag-page__error {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 4rem 2rem;
		text-align: center;
	}
	
	.tag-page__error h1 {
		font-size: 1.25rem;
		color: var(--text-secondary);
		margin: 1rem 0;
	}
	
	.tag-page__back-link {
		color: var(--brand-primary);
		text-decoration: none;
		font-size: 0.875rem;
		margin-top: 1rem;
		transition: opacity 0.2s ease;
	}
	
	.tag-page__back-link:hover {
		opacity: 0.8;
	}
	
	/* Title Section */
	.tag-page__title-section {
		margin-bottom: 2rem;
	}
	
	.tag-page__title {
		display: flex;
		align-items: center;
		gap: 1rem;
		margin: 0 0 0.5rem 0;
	}
	
	.tag-page__tag-badge {
		display: inline-flex;
		align-items: center;
		padding: 0.5rem 1rem;
		background: var(--brand-muted);
		color: var(--brand-primary);
		border-radius: 20px;
		font-size: 1.5rem;
		font-weight: 600;
	}
	
	.tag-page__count {
		font-size: 0.875rem;
		color: var(--text-secondary);
		margin: 0;
	}
	
	/* Sections */
	.tag-page__section-title {
		font-size: 0.75rem;
		font-weight: 600;
		color: var(--text-tertiary);
		text-transform: uppercase;
		letter-spacing: 0.1em;
		margin: 0 0 0.75rem 0;
	}
	
	/* Related Tags */
	.tag-page__related {
		margin-bottom: 2rem;
	}
	
	.tag-page__related-tags {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
	}
	
	.tag-page__related-tag {
		display: inline-flex;
		align-items: center;
		padding: 0.375rem 0.75rem;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: 14px;
		color: var(--text-secondary);
		text-decoration: none;
		font-size: 0.75rem;
		font-weight: 500;
		transition: all 0.2s ease;
	}
	
	.tag-page__related-tag:hover {
		background: var(--bg-tertiary);
		border-color: var(--brand-primary);
		color: var(--brand-primary);
		transform: translateY(-1px);
	}
	
	/* Articles List */
	.tag-page__articles {
		margin-bottom: 3rem;
	}
	
	.tag-page__articles-list {
		display: grid;
		gap: 0.75rem;
	}
	
	.article-card {
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: 8px;
		transition: all 0.2s ease;
	}
	
	.article-card:hover {
		border-color: var(--border-default);
		background: var(--bg-tertiary);
		transform: translateY(-1px);
	}
	
	.article-card__link {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 1rem 1.25rem;
		text-decoration: none;
		color: inherit;
	}
	
	.article-card__content {
		flex: 1;
		min-width: 0;
	}
	
	.article-card__title {
		font-size: 1rem;
		font-weight: 500;
		color: var(--text-primary);
		margin: 0 0 0.375rem 0;
		line-height: 1.4;
	}
	
	.article-card__description {
		font-size: 0.875rem;
		color: var(--text-secondary);
		margin: 0 0 0.5rem 0;
		line-height: 1.5;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
	
	.article-card__meta {
		display: flex;
		gap: 1rem;
		align-items: center;
	}
	
	.article-card__date {
		font-size: 0.75rem;
		color: var(--text-tertiary);
	}
	
	.article-card__arrow {
		color: var(--text-tertiary);
		flex-shrink: 0;
		transition: all 0.2s ease;
		opacity: 0;
	}
	
	.article-card:hover .article-card__arrow {
		opacity: 1;
		transform: translateX(2px);
		color: var(--brand-primary);
	}
	
	/* Dark mode */
	:global([data-theme='dark']) .tag-page__tag-badge {
		background: rgba(255, 94, 126, 0.15);
		color: #ff7a99;
	}
	
	:global([data-theme='dark']) .article-card {
		background: rgba(255, 255, 255, 0.02);
		border-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .article-card:hover {
		background: rgba(255, 255, 255, 0.04);
		border-color: rgba(255, 255, 255, 0.12);
	}
	
	:global([data-theme='dark']) .tag-page__related-tag {
		background: rgba(255, 255, 255, 0.04);
		border-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .tag-page__related-tag:hover {
		background: rgba(255, 94, 126, 0.1);
		border-color: #ff5e7e;
		color: #ff7a99;
	}
	
	/* Mobile */
	@media (max-width: 640px) {
		.tag-page {
			padding: 1.5rem 1rem;
		}
		
		.tag-page__breadcrumb {
			font-size: 0.75rem;
			flex-wrap: wrap;
		}
		
		.tag-page__tag-badge {
			font-size: 1.25rem;
			padding: 0.375rem 0.75rem;
		}
		
		.article-card__link {
			padding: 1rem;
		}
	}
</style>